import {createContext, useContext, useEffect, useMemo, useState, type ReactNode} from "react";
import {getShopSiteStatus, setApiDebugEnabled, type ShopAccessMode, type ShopSiteStatus} from "@corporatesclub/shared";
import {applySiteAccentColor} from "@/lib/applySiteAccent";
import {readSiteStatusCache, writeSiteStatusCache} from "@/lib/shopCache";

const DEFAULT_STATUS: ShopSiteStatus = {
  accentColor: "#ff4200",
  shopEnabled: true,
  ordersEnabled: true,
  closedMessage: "Magazinul este temporar inchis. Revino in curand.",
  accessMode: "login_required",
  publicAccess: false,
  guestPricesVisible: false,
  guestOrdersAllowed: false,
  debugErrors: false
};

function normalizeStatus(raw: Partial<ShopSiteStatus>): ShopSiteStatus {
  const accessMode = (raw.accessMode || (raw.publicAccess ? "public_full" : "login_required")) as ShopAccessMode;
  const publicAccess = raw.publicAccess === true || accessMode !== "login_required";
  return {
    ...DEFAULT_STATUS,
    ...raw,
    accessMode,
    publicAccess,
    guestPricesVisible: raw.guestPricesVisible === true || accessMode === "public_full" || accessMode === "public_login_to_order",
    guestOrdersAllowed: raw.guestOrdersAllowed === true || accessMode === "public_full",
    debugErrors: raw.debugErrors === true
  };
}

type Ctx = {
  status: ShopSiteStatus;
  ready: boolean;
  refresh: () => Promise<void>;
};

const ShopSiteContext = createContext<Ctx>({
  status: DEFAULT_STATUS,
  ready: false,
  refresh: async () => {}
});

function initialCached(): {status: ShopSiteStatus; ready: boolean} {
  const cached = readSiteStatusCache();
  if(!cached) return {status: DEFAULT_STATUS, ready: false};
  const status = normalizeStatus(cached);
  return {status, ready: true};
}

export function ShopSiteProvider({children}: {children: ReactNode}) {
  const [boot] = useState(initialCached);
  const [status, setStatus] = useState<ShopSiteStatus>(boot.status);
  const [ready, setReady] = useState(boot.ready);

  async function refresh() {
    try {
      const s = await getShopSiteStatus();
      const next = normalizeStatus(s);
      setStatus(next);
      writeSiteStatusCache(next);
      applySiteAccentColor(next.accentColor);
      setApiDebugEnabled(!!next.debugErrors);
    } catch {
      if(!boot.ready) applySiteAccentColor(DEFAULT_STATUS.accentColor);
    } finally {
      setReady(true);
    }
  }

  useEffect(() => {
    if(boot.ready) {
      applySiteAccentColor(boot.status.accentColor);
      setApiDebugEnabled(!!boot.status.debugErrors);
    }
    void refresh();
  }, []);

  const value = useMemo(() => ({status, ready, refresh}), [status, ready]);
  return <ShopSiteContext.Provider value={value}>{children}</ShopSiteContext.Provider>;
}

export function useShopSite() {
  return useContext(ShopSiteContext);
}
